import {
  ANY_CLASS_IRI,
  TopExpression,
  Variable,
} from 'vet-bones/utils/queryBuilder';

import { buildIfFilter } from 'src/utils/stringify/rules/buildIfFilter';
import { buildInequalityFilter } from 'src/utils/stringify/rules/buildInequalityFilter';
import { OBJECT_VARIABLE } from 'src/utils/stringify/rules/constants';
import { getIdsByIri } from 'src/utils/stringify/rules/getIdsByIri';

export const buildIfClause = ({
  abbreviate,
  getVariable,
  variablesById,
  instancesByIds,
  topExpression,
}: {
  abbreviate: (iri: string) => string;
  getVariable: (variable: string, id?: string) => string;
  variablesById: Record<string, string>;
  instancesByIds: Array<Record<string, string>>;
  topExpression: TopExpression;
}): string => {
  const blocks = instancesByIds.map((instancesById) => {
    const allTriples: string[][] = [];
    const idsByIri = getIdsByIri(topExpression);

    const addVariable = (
      variables: Set<string>,
      value: string | Variable | undefined
    ) => {
      if (typeof value !== 'string' && value?.id) {
        variables.add(value.id);
      }
    };

    const getInstanceIri = (subject: Variable): string =>
      instancesById[subject.id] || '';

    topExpression.expressions.forEach((subExpression) => {
      const andVariables = new Set<string>();
      const processedAndInstances = new Set<string>();
      const processedAndTypes = new Set<string>();

      subExpression.expressions.forEach((expression, idx) => {
        const processedVariables = new Set<string>();
        const processedInstances = new Set<string>();
        const processedTypes = new Set<string>();
        const triples: string[] = [];
        const typeTriples: string[] = [];
        const addTypeVariable = ({
          id,
          instanceIri,
          typeIri,
          variable,
        }: {
          id: string;
          instanceIri: string;
          typeIri: string;
          variable: string;
        }) => {
          if (processedAndTypes.has(id) || processedTypes.has(id)) {
            return;
          }

          processedTypes.add(id);
          if (!idx) {
            processedAndTypes.add(id);
          }

          if (typeIri !== ANY_CLASS_IRI) {
            const classTriple = `${
              instanceIri ? abbreviate(instanceIri) : variable
            } a ${abbreviate(typeIri)} .`;
            typeTriples.push(classTriple);
          }
        };

        expression.filters.forEach((filter) => {
          if (!idx) {
            addVariable(andVariables, filter.subject);
            addVariable(andVariables, filter.value);
            addVariable(andVariables, filter.comparisonSubject);
          }
          addVariable(processedVariables, filter.subject);
          addVariable(processedVariables, filter.value);
          addVariable(processedVariables, filter.comparisonSubject);

          if (
            !processedAndInstances.has(filter.subject.id) &&
            !processedInstances.has(filter.subject.id)
          ) {
            if (!idx) {
              processedAndInstances.add(filter.subject.id);
            }
            processedInstances.add(filter.subject.id);
            const subjectIri = getInstanceIri(filter.subject);
            const subjectVar = getVariable(OBJECT_VARIABLE, filter.subject.id);
            addTypeVariable({
              id: filter.subject.id,
              instanceIri: subjectIri,
              typeIri: filter.subject.iri,
              variable: subjectVar,
            });
          }

          const ifFilter = buildIfFilter({
            abbreviate,
            getInstanceIri,
            getVariable,
            filter,
          });

          if (ifFilter) {
            if (ifFilter.typeVariable) {
              addTypeVariable(ifFilter.typeVariable);
            }
            triples.push(ifFilter.triple);
          }
        });

        const inequalityFilter = buildInequalityFilter({
          abbreviate,
          idsByIri,
          instancesById,
          variablesById,
          variableIds: new Set([...andVariables, ...processedVariables]),
        });

        if (inequalityFilter) {
          triples.push(inequalityFilter);
        }

        allTriples.push(typeTriples.concat(triples));
      });
    });

    const [whereTriples, ...whereNotTriples] = allTriples;
    return [
      whereTriples.join('\n    '),
      ...whereNotTriples.map(
        (triples) => `FILTER NOT EXISTS {
      ${triples.join('\n      ')}
    }`
      ),
    ].join('\n\n    ');
  });

  return `IF {
  ${blocks
    .map(
      (block) => `{
    ${block}
  }`
    )
    .join(' UNION ')}
}`;
};
